import {useCallback, useMemo} from 'react';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {ROUTES} from '../routes';
import {useNotification} from '../hooks/use-notifications.tsx';
import {RoutePatternsService} from '../api/generated/RoutePatternsService.ts';
import {Link, useNavigate} from 'react-router-dom';
import {routePatternSchema, RoutePatternSchema} from './schemas.ts';
import {createForm} from '../forms';
import {BasicPage} from '../components/basic-page.tsx';
import {Button, Group, Text} from '@mantine/core';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {DeleteButton} from '../components/delete-button.tsx';
import {SubmitHandler} from 'react-hook-form';

const Form = createForm<RoutePatternSchema>();

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Route Patterns', href: ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path},
  {title: 'Create'},
];

export const RoutePatternCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<RoutePatternSchema>>(
    async (value) => {
      const response = await RoutePatternsService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Route Pattern Created');
      navigate(ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path);
    },
    [navigate, notifications]
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form schema={routePatternSchema} onSubmit={onSubmit}>
        <Form.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type="button"
              variant="outline"
              component={Link}
              to={ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Route Pattern</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const RoutePatternUpdate = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.ROUTE_PATTERNS.DETAIL);
  const navigate = useNavigate();
  const notifications = useNotification();

  const fetchRoutePattern = useAsync(async () => {
    const {data} = await RoutePatternsService.getById({
      id,
    });
    return data;
  }, [id]);

  const routePattern = fetchRoutePattern.value || undefined;
  const routePatternDescription = routePattern?.description ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Route Patterns', href: ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path},
      {title: routePatternDescription},
    ],
    [routePatternDescription]
  );

  const onSubmit = useCallback<SubmitHandler<RoutePatternSchema>>(
    async (value) => {
      const response = await RoutePatternsService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Route Pattern Updated');
      navigate(ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path);
    },
    [id, navigate, notifications]
  );

  const [deleteRoutePatternState, deleteRoutePattern] = useAsyncFn(async () => {
    const response = await RoutePatternsService.deleteById({id});
    if (response.hasErrors) {
      notifications.error('Unable to delete route pattern');
      return;
    }

    notifications.success('Route pattern successfully deleted');
    navigate(ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path);
  });

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchRoutePattern}>
        <Form
          schema={routePatternSchema}
          defaultValues={routePattern}
          onSubmit={onSubmit}
        >
          <Form.Container>
            <FormFields />
            <Group mt={24} justify={'space-between'}>
              <DeleteButton
                buttonLabel={'Delete Pattern'}
                confirmButtonLabel={'Delete Pattern'}
                onConfirm={deleteRoutePattern}
                loading={deleteRoutePatternState.loading}
                confirmTitle={
                  <Text fw={600}>
                    Are you sure you want to delete this pattern?
                  </Text>
                }
              />
              <Group>
                <Button
                  type="button"
                  variant="outline"
                  component={Link}
                  to={ROUTES.ADMIN.ROUTE_PATTERNS.INDEX.path}
                >
                  Cancel
                </Button>
                <Form.Submit>Update Route Pattern</Form.Submit>
              </Group>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  return (
    <>
      <Form.Row>
        <Form.TextInput name="description" label={'Description'} />
        <Form.TextInput name="pattern" label={'Pattern'} />
      </Form.Row>
    </>
  );
};